import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar } from "lucide-react";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { useCollection } from "../lib/useCollection";
import { resolveMediaUrl } from "../lib/mediaUrl";

const POSTS = [
    {
        id: "brand-redesign-signs",
        title: "5 Signs Your Brand Needs a Redesign",
        date: "April 2025",
        dateISO: "2025-04-12",
        category: "Branding",
        excerpt:
            "If your visual identity feels dated, inconsistent across touchpoints, or doesn't match where your business is today — these five signals tell you it's time.",
        readTime: "5 min read",
        seed: "rg-blog-redesign",
    },
    {
        id: "signage-walk-ins",
        title: "How Good Signage Can Double Your Walk‑in Customers",
        date: "March 2025",
        dateISO: "2025-03-18",
        category: "Signage",
        excerpt:
            "Storefront signage is the cheapest, most underrated marketing channel for local businesses. Here's how a thoughtful glow sign or acrylic facade can lift footfall noticeably.",
        readTime: "6 min read",
        seed: "rg-blog-signage",
    },
    {
        id: "logo-investment",
        title: "Why Logo Design is the Most Important Investment for a New Business",
        date: "February 2025",
        dateISO: "2025-02-21",
        category: "Logo Design",
        excerpt:
            "A logo isn't a graphic — it's the first contract you sign with your customer. We break down what makes a strong, scalable mark and why founders should never cut corners here.",
        readTime: "7 min read",
        seed: "rg-blog-logo",
    },
];

const imgFor = (seed) => `https://picsum.photos/seed/${encodeURIComponent(seed)}/900/560`;

const FALLBACK = POSTS.map((p) => ({
    id: p.id,
    title: p.title,
    date: p.date,
    date_iso: p.dateISO,
    category: p.category,
    excerpt: p.excerpt,
    image_url: imgFor(p.seed),
    read_time: p.readTime,
}));

const BlogPage = () => {
    const posts = useCollection("/blog", FALLBACK);
    useDocumentMeta({
        title: "Design & Branding Blog — Raj Graphics Nagpur",
        description:
            "Insights on branding, logo design, printing and signage from Raj Graphics — practical reads for founders and growing businesses in Nagpur and beyond.",
        canonical: "/blog",
    });

    return (
        <main data-testid="blog-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Studio Journal
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="blog-headline"
                    >
                        Notes on <span className="gold-gradient-text">brand, design</span> &amp; the work.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Practical, opinionated reads from the studio. No fluff — just things we
                        wish more founders knew before starting their brand.
                    </motion.p>
                </div>
            </section>

            {/* Posts grid */}
            <section className="relative py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div
                        data-testid="blog-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
                    >
                        {posts.map((p, i) => (
                            <motion.article
                                key={p.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.55, delay: i * 0.07 }}
                                data-testid={`blog-card-${p.id}`}
                                className="group flex flex-col bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden hover:border-gold/30 transition-colors"
                            >
                                <div className="relative h-56 overflow-hidden">
                                    <img
                                        src={resolveMediaUrl(p.image_url)}
                                        alt={p.title}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                                    <span className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white font-inter text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border border-white/15">
                                        {p.category}
                                    </span>
                                </div>

                                <div className="p-6 md:p-7 flex flex-col flex-1">
                                    <div className="flex items-center gap-3 font-inter text-xs text-zinc-500 mb-4">
                                        <Calendar size={13} className="text-gold" />
                                        <time dateTime={p.date_iso}>{p.date}</time>
                                        <span className="text-zinc-700">•</span>
                                        <span>{p.read_time}</span>
                                    </div>
                                    <h2 className="font-poppins font-medium text-white text-xl leading-snug line-clamp-2">
                                        {p.title}
                                    </h2>
                                    <p className="mt-3 font-inter text-sm text-zinc-400 leading-relaxed line-clamp-2 flex-1">
                                        {p.excerpt}
                                    </p>
                                    <Link
                                        to="/contact"
                                        data-testid={`blog-readmore-${p.id}`}
                                        className="mt-6 self-start group/btn inline-flex items-center gap-2 text-gold font-inter text-sm hover:underline underline-offset-4"
                                    >
                                        Read More
                                        <ArrowUpRight
                                            size={15}
                                            className="transition-transform group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5"
                                        />
                                    </Link>
                                </div>
                            </motion.article>
                        ))}
                    </div>

                    <p className="mt-12 font-inter text-sm text-zinc-500">
                        More writing coming soon. Want a topic covered?{" "}
                        <a
                            href="/contact"
                            className="text-gold hover:underline underline-offset-4"
                        >
                            Drop us a note.
                        </a>
                    </p>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default BlogPage;
